/**
 * Session resolution — pure.
 *
 * Answers, from what the database returned: which shop am I in, what may I do,
 * what language do I read, and how is money written here.
 *
 * Kept out of React on purpose. Every rule below is one somebody will argue
 * about later, and arguments are cheaper against a test than against a
 * component.
 */

import { COUNTRIES, type CountrySpec } from '../intl/locale.js';
import { LANGUAGES, direction } from '../intl/i18n.js';

export type Role = 'owner' | 'estimator' | 'technician';
export type MembershipStatus = 'invited' | 'active' | 'disabled';

export interface AuthUser {
  id: string;
  email: string;
  /** The person's own choice. Overrides the shop default (FR51). */
  language?: string | null;
}

export interface Shop {
  id: string;
  name: string;
  countryCode: string;
  defaultLanguage: string;
  status: 'active' | 'suspended' | 'closed';
}

export interface Membership {
  shopId: string;
  role: Role;
  status: MembershipStatus;
}

export interface ResolvedSession {
  user: AuthUser;
  shop: Shop;
  role: Role;
  country: CountrySpec;
  /** Language actually in force for this person. */
  language: string;
  dir: 'ltr' | 'rtl';
  /** Shops this person could switch to. */
  availableShops: Shop[];
  can: {
    issueEstimate: boolean;
    editRates: boolean;
    editCatalog: boolean;
    manageUsers: boolean;
    seePrices: boolean;
  };
}

export type SessionOutcome =
  | { kind: 'signed_out' }
  /**
   * We hold a credential but could not READ the session. Deliberately distinct
   * from `signed_out`: treating a failed read as a sign-out looks, to the
   * estimator, like being logged out at random on a bad connection — mid-job,
   * at a vehicle. This state says "try again", not "start again".
   */
  | { kind: 'load_failed'; message: string }
  | { kind: 'no_membership'; user: AuthUser }
  | { kind: 'shop_unavailable'; user: AuthUser; reason: 'suspended' | 'closed' }
  | { kind: 'ready'; session: ResolvedSession };

/**
 * Permissions.
 *
 * A technician sees the work, not the money — that is the whole point of a work
 * order being a separate document. `issueEstimate` is deliberately open to an
 * estimator: the PRD leaves "may a junior issue without the owner" open, and the
 * permissive default is the one that matches how a small shop actually runs. It
 * is one line to tighten when that question is settled.
 */
export function permissionsFor(role: Role): ResolvedSession['can'] {
  switch (role) {
    case 'owner':
      return { issueEstimate: true, editRates: true, editCatalog: true,
               manageUsers: true, seePrices: true };
    case 'estimator':
      return { issueEstimate: true, editRates: false, editCatalog: true,
               manageUsers: false, seePrices: true };
    case 'technician':
      return { issueEstimate: false, editRates: false, editCatalog: false,
               manageUsers: false, seePrices: false };
  }
}

function isKnownLanguage(code: string | null | undefined): code is string {
  return typeof code === 'string' && LANGUAGES.some((l) => l.code === code);
}

/**
 * Language in force, most specific first:
 *   the person's own choice → the shop default → English.
 *
 * An unknown code is ignored rather than trusted, so a stale value in the
 * database cannot leave someone staring at a blank interface.
 */
export function effectiveLanguage(user: AuthUser, shop: Shop): string {
  if (isKnownLanguage(user.language)) return user.language;
  if (isKnownLanguage(shop.defaultLanguage)) return shop.defaultLanguage;
  return 'en';
}

export interface ResolveInput {
  user: AuthUser | null;
  memberships: Membership[];
  shops: Shop[];
  /** Shop the person last used, if the client remembered one. */
  preferredShopId?: string | null;
}

/**
 * Work out the session, or say precisely why there isn't one.
 *
 * Returns an outcome rather than throwing or returning null, so every caller is
 * forced to handle the invited-but-not-active and suspended-shop cases. Both
 * happen in normal operation and both need their own screen.
 */
export function resolveSession(input: ResolveInput): SessionOutcome {
  const { user, memberships, shops, preferredShopId } = input;
  if (!user) return { kind: 'signed_out' };

  const active = memberships.filter((m) => m.status === 'active');
  if (active.length === 0) return { kind: 'no_membership', user };

  const byId = new Map(shops.map((s) => [s.id, s]));
  const joinable = active
    .map((m) => ({ m, shop: byId.get(m.shopId) }))
    .filter((x): x is { m: Membership; shop: Shop } => Boolean(x.shop));

  if (joinable.length === 0) return { kind: 'no_membership', user };

  const usable = joinable.filter((x) => x.shop.status === 'active');
  if (usable.length === 0) {
    const first = joinable[0]!;
    return {
      kind: 'shop_unavailable',
      user,
      reason: first.shop.status === 'closed' ? 'closed' : 'suspended',
    };
  }

  const chosen =
    usable.find((x) => x.shop.id === preferredShopId) ??
    // Stable rather than "whatever the database returned first": a person who
    // belongs to two shops should land in the same one every morning.
    [...usable].sort((a, b) => a.shop.name.localeCompare(b.shop.name))[0]!;

  const spec = COUNTRIES[chosen.shop.countryCode];
  if (!spec) {
    // A shop with an unknown country cannot format money, and formatting money
    // wrongly is worse than refusing to open.
    return { kind: 'shop_unavailable', user, reason: 'suspended' };
  }

  const lang = effectiveLanguage(user, chosen.shop);

  return {
    kind: 'ready',
    session: {
      user,
      shop: chosen.shop,
      role: chosen.m.role,
      country: spec,
      language: lang,
      dir: direction(lang),
      availableShops: usable.map((x) => x.shop).sort((a, b) => a.name.localeCompare(b.name)),
      can: permissionsFor(chosen.m.role),
    },
  };
}
